import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { CamelCasedPropertiesDeep } from "type-fest";
import { recursiveSnakeCaseCipherTypesafe } from "../../postgrestApi/common";
import { useInvalidateSiteQueries } from "../../sites/sitesQueries";
import FetchClients, { ClientOutput } from "../client";
import { BackpackServiceError, isBackpackResponseErrorBody } from "../errors";

type PatchSiteRequestBody = CamelCasedPropertiesDeep<
  ClientOutput["site"]["PatchSiteRequest"]
>;

const sites = {
  mutations: {
    usePatch,
  },
} as const;

export default sites;

// endpoint requires {feature: "Building Overview", access: "edit"}
export const patchSite = async (
  siteId: string | number,
  body: PatchSiteRequestBody,
  {
    checkFeatureAccessHeader,
    throwOnError = true,
  }: {
    checkFeatureAccessHeader: CheckFeatureAccessHeader;
    throwOnError?: boolean;
  }
) => {
  const { response, error } = await FetchClients.site.PATCH("/{id}", {
    body: recursiveSnakeCaseCipherTypesafe(body),
    params: {
      path: { id: Number(siteId) },
    },
    headers: checkFeatureAccessHeader,
  });
  if (response.ok || !throwOnError) return;
  if (isBackpackResponseErrorBody(error)) {
    throw new BackpackServiceError(error);
  }
  throw new Error(error);
};

function usePatch(
  siteId: number,
  { superAdminOnly = false }: { superAdminOnly?: boolean } = {}
) {
  const { refreshSiteQueries } = useInvalidateSiteQueries({ siteId });
  return useMutationWithAccessCheckLog(
    ["Building Overview", "edit", superAdminOnly],
    (site: PatchSiteRequestBody, checkFeatureAccessHeader) =>
      patchSite(siteId, site, { checkFeatureAccessHeader }),
    { onSuccess: () => refreshSiteQueries() }
  );
}
